import { _lang } from '@umu/ukit-lang';
import { lang } from '@umu/ukit-lang';
import { v4 as uuIdv4 } from 'uuid';
import React, { RefObject, useState /*, useEffect*/, useRef } from 'react';
import { classSet, $document } from '@umu/umu-dom';
import { customFormatDateTime } from '@umu/umu-util';
import { DateRangePicker, Range, OnChangeProps } from 'react-date-range';
import { components } from 'common';
import { Locale } from 'date-fns';

import { getTodayStart, getTodayEnd } from '@umu/umu-util';
import ReactDOM from 'react-dom';

import './datePicker.scss';
import './index.scss';

import cn from 'date-fns/locale/zh-CN';
import en from 'date-fns/locale/en-US';
import ja from 'date-fns/locale/ja';
import tw from 'date-fns/locale/zh-TW';

const { If } = components;
interface IDateValue {
    startDate: number;
    endDate: number;
}

interface IStyle {
    zIndex?: string;
    iconType?: 'icon-cal' | 'icon-up';
}
export interface IProps {
    cls?: string;
    style?: IStyle;
    dateValue: IDateValue;
    isNoAllowClear?: boolean;
    isSelected?: boolean;
    disabledDay?: (date: Date) => boolean;
    onDateRangeChange: (startDate: number, endDate: number) => void;
}
export interface IRangePickerProps {
    pickerRef: RefObject<HTMLDivElement>;
    idPicker: string;
    dateValue: IDateValue;
    onCancel: () => void;
    onConfirm: (data: Range) => void;
    onDisabledDay: (date: Date) => boolean;
}
const localeConfig = {
    locale: en as Locale,
    dateDisplayFormat: 'MMM d, yyyy',
};

if (_lang('CN')) {
    localeConfig.locale = cn;
    localeConfig.dateDisplayFormat = 'yyyy-MM-dd';
}

if (_lang('EN')) {
    localeConfig.locale = en;
}

if (_lang('JP')) {
    localeConfig.locale = ja;
}

if (_lang('TW')) {
    localeConfig.locale = tw;
}

const getLocalDateByLocal = (time: number, label: string) => {
    return !!time ? customFormatDateTime(time, 'yyyy-MM-dd') : label;
};
const getTransformTime = (data: IDateValue) => {
    return {
        startDate: data.startDate ? new Date(data.startDate) : getTodayStart(),
        endDate: data.endDate ? new Date(data.endDate) : getTodayEnd(),
    };
};
export const RangePicker = (props: IRangePickerProps) => {
    const { pickerRef, dateValue, onDisabledDay, onCancel, onConfirm } = props;
    const [dateRange, setDateRange] = useState<OnChangeProps>(getTransformTime(dateValue));
    return (
        <div className="date-range-picker-panel" ref={pickerRef}>
            <DateRangePicker
                className="date-range-picker"
                onChange={(val: any) => {
                    setDateRange(val.range1);
                }}
                showSelectionPreview={false}
                moveRangeOnFirstSelection={false}
                months={2}
                disabledDay={onDisabledDay}
                direction="horizontal"
                ranges={[dateRange as Range]}
                locale={localeConfig.locale}
                startDatePlaceholder=""
                endDatePlaceholder=""
                dateDisplayFormat={localeConfig.dateDisplayFormat}
                rangeColors={['#2197F3']}
            />
            <div className="footer">
                <span className="footer-btn footer-btn-cancel" onClick={onCancel}>
                    {lang('取消')}
                </span>
                <span className="footer-btn footer-btn-confirm" onClick={() => onConfirm(dateRange as Range)}>
                    {lang('确定')}
                </span>
            </div>
        </div>
    );
};

const getElementPickerStyle = async (
    ele: HTMLDivElement,
    elementRef: RefObject<HTMLDivElement>,
    pickerRef: RefObject<HTMLDivElement>,
    style?: IStyle,
) => {
    const current = elementRef.current;
    if (!!current && pickerRef.current) {
        const clientRect = current.getBoundingClientRect();
        const eleClientRect = pickerRef.current.getBoundingClientRect();

        const boyClientRect = $document.body.getBoundingClientRect();
        const isShowDirectRight = boyClientRect.width - clientRect.left > eleClientRect.width;
        const isShowBottom = boyClientRect.height - clientRect.top > eleClientRect.height;
        const leftOffset = isShowDirectRight
            ? clientRect.left
            : clientRect.left + clientRect.width - eleClientRect.width;
        const bottomOffset = isShowBottom ? clientRect.top + clientRect.height : clientRect.top - eleClientRect.height;

        ele.style.position = 'absolute';
        ele.style.left = `${leftOffset}px`;
        ele.style.zIndex = (style && style.zIndex) || '3000';
        ele.style.top = bottomOffset > 0 ? `${bottomOffset}px` : '0px';
        ele.style.display = '';
    }
};
const setPickerDisplay = (elementId: string, isShow: boolean) => {
    const ele = $document.getElementById(elementId);
    if (!!ele) {
        ele.style.display = isShow ? '' : 'none';
    }
};
const getPickerDisplay = (elementId: string): boolean => {
    const ele = $document.getElementById(elementId);
    return !!ele && !!(ele.style.display === '');
};
export function RangPicker(props: IProps) {
    const { onDateRangeChange, dateValue, isNoAllowClear = false, disabledDay, style, cls, isSelected } = props;
    const [idPicker, setIdPicker] = useState(uuIdv4());

    const elementRef: RefObject<HTMLDivElement> = useRef(null);
    const pickerRef: RefObject<HTMLDivElement> = useRef(null);
    const onPickerShow = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        const ele = $document.getElementById(idPicker);
        if (ele) {
            setPickerDisplay(idPicker, true);
        } else {
            createMsgPanel();
        }
    };

    const onConfirm = (data: Range) => {
        setPickerDisplay(idPicker, false);
        const st = getTodayStart(data.startDate);
        const ed = getTodayEnd(data.endDate);
        const start = st ? Math.ceil(st.valueOf()) : 0;
        const end = ed ? Math.ceil(ed.valueOf()) : 0;
        onDateRangeChange(start, end);
    };

    const onCancel = () => {
        setPickerDisplay(idPicker, false);
    };

    const createMsgPanel = async () => {
        const ele = $document.getElementById(idPicker) as HTMLDivElement;
        if (ele) {
            $document.body.removeChild(ele);
            createMsgPanel();
            return;
        }
        const element = $document.createElement('div');
        element.id = idPicker ? idPicker : uuIdv4();

        ReactDOM.render(
            <RangePicker
                pickerRef={pickerRef}
                idPicker={idPicker}
                dateValue={dateValue}
                onDisabledDay={disabledDay || setDisabledDay}
                onCancel={onCancel}
                onConfirm={onConfirm}
            />,
            element,
        );
        $document.body.appendChild(element);
        setTimeout(() => {
            getElementPickerStyle(element, elementRef, pickerRef, style);
        }, 200);
        setIdPicker(element.id);
    };

    const nowDate = Math.ceil(getTodayEnd().valueOf() / 1000);
    const setDisabledDay = (date: Date) => {
        const value = Math.ceil(date.valueOf() / 1000);
        return value >= nowDate;
    };
    const onClearDate = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        onDateRangeChange(0, 0);
    };

    const className = `${cls || ''} range-picker`;
    const iconCalSelect = isSelected || typeof isSelected === 'undefined';
    const iconCls: { [name: string]: boolean } =
        style?.iconType === 'icon-cal'
            ? {
                  'icon-cal-select': iconCalSelect,
                  'icon-cal-not': !iconCalSelect,
              }
            : {
                  'icon-down': getPickerDisplay(idPicker),
                  'icon-up': true,
              };
    return (
        <div className={className} ref={elementRef}>
            <div className="picker-label" onClick={onPickerShow}>
                <span
                    className={classSet({
                        'date-empty': !!!dateValue.startDate,
                        'date-time': true,
                    })}
                >
                    {getLocalDateByLocal(dateValue.startDate, lang('开始日期'))}
                </span>
                <span className="date-span">-</span>
                <span
                    className={classSet({
                        'date-empty': !!!dateValue.endDate,
                        'date-time': true,
                    })}
                >
                    {getLocalDateByLocal(dateValue.endDate, lang('结束日期'))}
                </span>
                <span className="date-end">
                    <If condition={!!(dateValue.startDate || !!dateValue.endDate) && !isNoAllowClear}>
                        <i className="date-icon-clear" title={lang('清空')} onClick={onClearDate} />
                    </If>
                    <i className={classSet(iconCls)} />
                </span>
            </div>
        </div>
    );
}
